import { useEffect, useState } from "react";
import {
  portfolio as portfolioApi,
  type AccountWithValue,
  type InstallmentResult,
  type SavingsFrequency,
  type SavingsPlan,
  type SavingsPlanLine,
} from "../../api";
import { formatCurrency } from "./format";

const FREQUENCY_LABEL: Record<SavingsFrequency, string> = {
  monthly: "month",
  quarterly: "quarter",
  yearly: "year",
};

const FREQUENCIES: SavingsFrequency[] = ["monthly", "quarterly", "yearly"];

// A savings agreement's installment total — the monetary lines only, since a
// unit-denominated line has no price until the order prices it.
const installmentTotal = (lines: SavingsPlanLine[]) =>
  lines.filter((l) => l.size_type === "amount").reduce((sum, l) => sum + l.size, 0);

// The client's savings agreements (spareavtale), on their portfolio page: what
// they pay in, into what, and when the next installment falls. Editing pushes
// the change back to Quantfolio, which holds the same agreement.
export function SavingsPlans({
  investorId,
  accounts,
}: {
  investorId: number;
  accounts: AccountWithValue[];
}) {
  const [plans, setPlans] = useState<SavingsPlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  function load() {
    portfolioApi
      .savingsPlans(investorId)
      .then((d) => setPlans(d.savings_plans))
      .catch((e) => setError((e as Error).message));
  }
  useEffect(load, [investorId]);

  // Runs one specific period, never "whatever is next" — the advisor is told
  // which one, and warned when it hasn't fallen due yet. Buying a period early
  // is legitimate (it's how you demo the schedule), but it must be deliberate:
  // each run advances the agreement, so a second click buys the period after.
  async function runNow(plan: SavingsPlan, dueDate: string, retry = false) {
    const total = formatCurrency(installmentTotal(plan.lines), plan.currency);
    const early = !retry && dueDate > todayIso();
    const question = retry
      ? `Retry the ${dueDate} installment for "${plan.name}"? It will buy ${total} into ${plan.account_name ?? "the linked account"}.`
      : `Buy the ${dueDate} installment for "${plan.name}" now — ${total} into ${plan.account_name ?? "the linked account"}?` +
        (early ? `\n\nThat period isn't due yet. The agreement then moves on to the period after it.` : "");
    if (!window.confirm(question)) return;
    setBusy(plan.id);
    setError(null);
    setNotice(null);
    try {
      const { result } = await portfolioApi.runSavingsInstallment(plan.id, {
        due_date: dueDate,
        force: retry,
      });
      setNotice(describeRun(result, plan.currency));
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function cancel(plan: SavingsPlan) {
    if (
      !window.confirm(
        `Cancel "${plan.name}"? The agreement stops here and is removed from Quantfolio too. Installments already bought are unaffected.`,
      )
    )
      return;
    setBusy(plan.id);
    setError(null);
    setNotice(null);
    try {
      const r = await portfolioApi.cancelSavingsPlan(plan.id);
      setNotice(
        r.quantfolio_error
          ? `Cancelled at Andersen, but Quantfolio rejected it: ${r.quantfolio_error}`
          : "Agreement cancelled, in Quantfolio too.",
      );
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (plans === null) return null;

  return (
    <>
      <h2 className="section-title">Savings plans</h2>
      {notice && <div className="banner-success">{notice}</div>}
      {error && <div className="banner-error">{error}</div>}

      {plans.length === 0 ? (
        <p className="muted">
          No savings agreements. They arrive from Quantfolio as recurring orders and
          are set up from the Inbox.
        </p>
      ) : (
        plans.map((plan) =>
          editing === plan.id ? (
            <PlanEditor
              key={plan.id}
              plan={plan}
              accounts={accounts}
              onClose={() => setEditing(null)}
              onSaved={(message) => {
                setEditing(null);
                setNotice(message);
                setError(null);
                load();
              }}
            />
          ) : (
            <PlanCard
              key={plan.id}
              plan={plan}
              busy={busy === plan.id}
              onEdit={() => {
                setEditing(plan.id);
                setNotice(null);
                setError(null);
              }}
              onRun={(due, retry) => runNow(plan, due, retry)}
              onCancel={() => cancel(plan)}
            />
          ),
        )
      )}
    </>
  );
}

const todayIso = () => new Date().toISOString().slice(0, 10);

function describeRun(r: InstallmentResult, currency: string): string {
  if (r.status === "skipped") return `Nothing run: ${r.error ?? "not due."}`;
  const head = `${r.dueDate}: ${r.ordersCreated} order${r.ordersCreated === 1 ? "" : "s"} placed, ${formatCurrency(r.totalAmount, currency)}`;
  const paid = r.funded > 0 ? ` (${formatCurrency(r.funded, currency)} debited from the client's bank)` : "";
  return r.error ? `${head}${paid}. Problems — ${r.error}` : `${head}${paid}.`;
}

function PlanCard({
  plan,
  busy,
  onEdit,
  onRun,
  onCancel,
}: {
  plan: SavingsPlan;
  busy: boolean;
  onEdit: () => void;
  onRun: (dueDate: string, retry?: boolean) => void;
  onCancel: () => void;
}) {
  const cancelled = plan.status === "cancelled";
  const recent = plan.installments.slice(0, 3);
  return (
    <section className="savings-plan">
      <header className="savings-plan-head">
        <div>
          <h3 className="savings-plan-name">
            {plan.name}
            <span className={`pill savings-status-${plan.status}`}>{plan.status}</span>
          </h3>
          <p className="muted savings-plan-terms">
            {formatCurrency(installmentTotal(plan.lines), plan.currency)} per{" "}
            {FREQUENCY_LABEL[plan.frequency]} · from {plan.start_date.slice(0, 10)}
            {plan.end_date ? ` to ${plan.end_date.slice(0, 10)}` : ""}
            {!cancelled && plan.next_run_date
              ? ` · next installment ${plan.next_run_date}`
              : ""}
          </p>
          <p className="muted savings-plan-account">
            Debits <strong>{plan.account_name ?? "— no account linked"}</strong>
            {plan.account_name ? " — installments buy into that account." : ""}
          </p>
        </div>
        <div className="savings-plan-actions">
          {!cancelled && (
            <>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit} disabled={busy}>
                Edit
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => plan.next_run_date && onRun(plan.next_run_date)}
                disabled={busy || !plan.next_run_date}
                title={
                  plan.next_run_date
                    ? `Buy the ${plan.next_run_date} installment now instead of waiting for the due date`
                    : "No installment scheduled"
                }
              >
                {busy ? "Running…" : `Run ${plan.next_run_date ?? "installment"}`}
              </button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} disabled={busy}>
                Cancel
              </button>
            </>
          )}
        </div>
      </header>

      <table className="table">
        <thead>
          <tr>
            <th>Fund</th>
            <th>ISIN</th>
            <th className="num">Per installment</th>
          </tr>
        </thead>
        <tbody>
          {plan.lines.map((l) => (
            <tr key={l.id}>
              <td className="strong">{l.name ?? l.isin ?? "—"}</td>
              <td className="muted">{l.isin ?? "—"}</td>
              <td className="num">
                {l.size_type === "units"
                  ? `${l.size} units`
                  : formatCurrency(l.size, plan.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {recent.length > 0 && (
        <ul className="savings-runs">
          {recent.map((i) => (
            <li key={i.id}>
              <span className={`pill savings-run-${i.status}`}>{i.status}</span>
              <span className="savings-run-date">{i.due_date}</span>
              <span className="muted">
                {i.orders_created} order{i.orders_created === 1 ? "" : "s"} ·{" "}
                {formatCurrency(i.total_amount, plan.currency)}
                {i.error ? ` · ${i.error}` : ""}
              </span>
              {i.status !== "executed" && !cancelled && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => onRun(i.due_date, true)}
                  disabled={busy}
                  title="Buy this period again — for one that was blocked, typically on funding"
                >
                  Retry
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Edit the terms and the composition. Amounts, frequency and dates cover what
// changes in practice; adding a fund needs the instrument picker and is done by
// re-posting the agreement from the Inbox.
function PlanEditor({
  plan,
  accounts,
  onClose,
  onSaved,
}: {
  plan: SavingsPlan;
  accounts: AccountWithValue[];
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [name, setName] = useState(plan.name);
  const [frequency, setFrequency] = useState<SavingsFrequency>(plan.frequency);
  const [startDate, setStartDate] = useState(plan.start_date.slice(0, 10));
  const [endDate, setEndDate] = useState(plan.end_date?.slice(0, 10) ?? "");
  const [accountId, setAccountId] = useState<number | null>(plan.account_id);
  const [lines, setLines] = useState(plan.lines);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (lines.length === 0) {
      setError("An agreement needs at least one fund.");
      return;
    }
    if (lines.some((l) => !Number.isFinite(l.size) || l.size <= 0)) {
      setError("Every fund needs a positive amount.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await portfolioApi.updateSavingsPlan(plan.id, {
        name,
        frequency,
        start_date: startDate,
        end_date: endDate || null,
        account_id: accountId,
        lines: lines.map((l) => ({
          isin: l.isin,
          name: l.name,
          size_type: l.size_type,
          size: l.size,
        })),
      });
      onSaved(
        r.quantfolio_error
          ? `Saved at Andersen, but Quantfolio rejected the change: ${r.quantfolio_error}`
          : "Agreement updated, and pushed to Quantfolio.",
      );
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <section className="savings-plan savings-plan-editing">
      <h3 className="savings-plan-name">Edit {plan.name}</h3>
      {error && <div className="banner-error">{error}</div>}

      <div className="field-row">
        <label className="field field-grow">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span>Frequency</span>
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as SavingsFrequency)}
          >
            {FREQUENCIES.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Start</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </label>
        <label className="field">
          <span>End (optional)</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </label>
      </div>

      <label className="field">
        <span>Debits account</span>
        <select
          value={accountId == null ? "" : String(accountId)}
          onChange={(e) => setAccountId(e.target.value === "" ? null : Number(e.target.value))}
        >
          <option value="">— none —</option>
          {accounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} ({a.currency})
            </option>
          ))}
        </select>
      </label>

      <table className="table">
        <thead>
          <tr>
            <th>Fund</th>
            <th className="num">Per installment</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={l.id}>
              <td className="strong">
                {l.name ?? l.isin ?? "—"}
                <span className="muted savings-line-isin">{l.isin}</span>
              </td>
              <td className="num">
                <input
                  className="savings-amount-input"
                  type="number"
                  min="0"
                  step="100"
                  value={l.size}
                  onChange={(e) =>
                    setLines((ls) =>
                      ls.map((x, j) => (j === i ? { ...x, size: Number(e.target.value) } : x)),
                    )
                  }
                />
              </td>
              <td className="num">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="field-hint">
        Installments buy into the account above and are funded from its cash, topped
        up by its funding mandate. Changing the frequency or start date re-derives
        the next installment date. Saving replaces the agreement's composition in
        Quantfolio too.
      </p>
      <div className="savings-plan-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>
          Discard
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save agreement"}
        </button>
      </div>
    </section>
  );
}
